/**
 * Pi-guided aircraft overlay — Overwatch as the ground page of the aircraft's Pi.
 *
 * Mounted inside `.video-wrapper` whenever the panel shows a `picam` source, the way
 * `PtzControl` is. Policy lives in `picam.ts`; this file is presentation and I/O only.
 * docs/PICAM_GUIDANCE.md.
 *
 * Targeting is the Pi page's: while CUT, a drag on the video IS the box — no TARGET mode.
 * The controls sit in a bar BELOW the picture (portalled into `barSlot`), because over
 * it they covered the lower-left, where a ground target usually is. In full screen only
 * the wrapper is shown, so the bar moves inside it — CUT must never be off screen.
 *
 * Two things here are deliberate and worth not "improving":
 *
 *  * **The heartbeat is this tab's, and only while it is visible.** The Pi CUTs after
 *    1 s without hearing its ground page, and that is what stops an aircraft flying a
 *    box nobody is watching. So it stops on unmount and on `visibilitychange` — exactly
 *    what closing or backgrounding the Pi's own page does. Never move it to a backend
 *    timer, a service worker or a shared tab.
 *  * **CUT is always enabled.** Stopping must never wait for a status poll.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { contentRect, toNormalized, toClient, ContentRect, NormPoint } from './roiGeometry';
import {
  HEARTBEAT_MS, STATUS_POLL_MS, PicamStatus, Tone, aircraftState, boxFromDrag, canDrawBox,
  describeCommand, friendlyReason, headlineState, modeMismatch, vmaxEditable,
} from './picam';

const FALLBACK_FRAME = { width: 640, height: 480 };   // 4:3 — never the reader's 640x360

const TONE: Record<Tone, string> = {
  danger: 'var(--color-danger)',
  warning: 'var(--color-warning)',
  accent: 'var(--color-accent)',
  info: 'var(--color-info)',
  muted: 'var(--color-text-muted)',
};

export interface PicamControlProps {
  wrapperRef: React.RefObject<HTMLDivElement>;
  apiUrl: string;
  authHeaders: (contentType?: boolean) => HeadersInit;
  /** Where the control bar renders, below the picture. Null → inside the wrapper. */
  barSlot: HTMLElement | null;
  /** The wrapper is full screen: the slot below it is not on screen. */
  fullscreen: boolean;
}

export default function PicamControl({ wrapperRef, apiUrl, authHeaders, barSlot, fullscreen }: PicamControlProps) {
  const [status, setStatus] = useState<PicamStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [heartbeatOk, setHeartbeatOk] = useState<boolean | null>(null);
  const [heartbeatWhy, setHeartbeatWhy] = useState<string | null>(null);
  const [dragA, setDragA] = useState<NormPoint | null>(null);
  const [dragB, setDragB] = useState<NormPoint | null>(null);
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [frame, setFrame] = useState(FALLBACK_FRAME);
  // null = follow the panel size: details hidden on the small side panel, shown once
  // popped out or full screen. The bar is below the picture either way.
  const [collapsedChoice, setCollapsedChoice] = useState<boolean | null>(null);
  const [narrow, setNarrow] = useState(true);
  const collapsed = collapsedChoice ?? narrow;
  const authRef = useRef(authHeaders);
  authRef.current = authHeaders;

  // --- keep the drawing surface glued to the picture ---------------------- //
  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    const w = img?.naturalWidth || status?.frame?.w || FALLBACK_FRAME.width;
    const h = img?.naturalHeight || status?.frame?.h || FALLBACK_FRAME.height;
    setFrame((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    setRect(contentRect(wrapper.clientWidth, wrapper.clientHeight, w, h));
    setNarrow(wrapper.clientWidth < 520);
  }, [wrapperRef, status?.frame?.w, status?.frame?.h]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- status ------------------------------------------------------------- //
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/picam/status`, { headers: authRef.current() });
        const body = res.ok ? await res.json() : { enabled: false, reason: `status ${res.status}` };
        if (!cancelled) setStatus(body);
      } catch {
        if (!cancelled) setStatus((prev) => (prev ? { ...prev, reachable: false, reason: 'Could not reach the server' } : prev));
      }
    };
    poll();
    const t = setInterval(poll, STATUS_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [apiUrl]);

  // --- the ground heartbeat (see the header comment) ----------------------- //
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const beat = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/picam/alive`, { method: 'POST', headers: authRef.current() });
        const body = res.ok ? await res.json().catch(() => null) : null;
        setHeartbeatOk(!!body?.ok);
        setHeartbeatWhy(body?.reason ?? (res.status === 403 ? 'Only an admin can keep guidance engaged' : null));
      } catch {
        setHeartbeatOk(false);
      }
    };
    const sync = () => {
      const visible = document.visibilityState === 'visible';
      if (visible && !timer) { beat(); timer = setInterval(beat, HEARTBEAT_MS); }
      if (!visible && timer) { clearInterval(timer); timer = null; setHeartbeatOk(null); }
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      if (timer) clearInterval(timer);
    };
  }, [apiUrl]);

  // --- commands ----------------------------------------------------------- //
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(t);
  }, [message]);

  const call = useCallback(async (path: string, body?: unknown): Promise<boolean> => {
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/picam/${path}`, {
        method: 'POST',
        headers: authRef.current(body !== undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        setMessage(detail?.detail || `Request failed (${res.status})`);
        return false;
      }
      return true;
    } catch {
      setMessage('Could not reach the server');
      return false;
    }
  }, [apiUrl]);

  const pointAt = useCallback((e: React.PointerEvent): NormPoint | null => {
    const wrapper = wrapperRef.current;
    if (!wrapper || rect.width <= 0) return null;
    const box = wrapper.getBoundingClientRect();
    return toNormalized(e.clientX - box.left, e.clientY - box.top, rect);
  }, [rect, wrapperRef]);

  const drawable = canDrawBox(status);
  useEffect(() => {
    // Resumed (or lost the Pi) mid-drag: the surface is gone, so is the half-drawn box.
    if (!drawable) { setDragA(null); setDragB(null); }
  }, [drawable]);
  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drawable) return;
    const p = pointAt(e);
    if (!p) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragA(p);
    setDragB(p);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragA) return;
    const p = pointAt(e);
    if (p) setDragB(p);
  };
  const onUp = async (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragA) return;
    const p = pointAt(e) || dragB;
    setDragA(null);
    setDragB(null);
    if (!p) return;
    const box = boxFromDrag(dragA, p, frame.width, frame.height);
    if (!box) return;            // a plain click is not a box; it does nothing
    if (await call('lock', box)) setMessage('Box sent — locking…');
  };

  // --- render ------------------------------------------------------------- //
  const head = headlineState(status);
  const ac = aircraftState(status);
  const g = status?.guidance;
  const preview = dragA && dragB ? (() => {
    const [x1, y1] = toClient(Math.min(dragA[0], dragB[0]), Math.min(dragA[1], dragB[1]), rect);
    const [x2, y2] = toClient(Math.max(dragA[0], dragB[0]), Math.max(dragA[1], dragB[1]), rect);
    return { left: x1, top: y1, width: x2 - x1, height: y2 - y1 };
  })() : null;
  const usable = !!status?.enabled && !!status?.reachable;
  const choices = status?.vmax_choices ?? null;
  const canVmax = vmaxEditable(status);
  const setVmax = async (v: number) => {
    if (await call('vmax', { v })) setMessage(`vmax ${v} m/s — applies from the next RESUME`);
  };
  const vmaxSelect = choices ? (
    <select
      aria-label="Approach speed limit"
      value={status?.vmax != null ? String(status.vmax) : ''}
      disabled={!canVmax}
      onChange={(e) => setVmax(Number(e.target.value))}
      title={canVmax ? 'Approach speed limit (m/s) — fixed list' : 'vmax can only change while CUT'}
      style={{ background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)',
               border: '1px solid var(--color-border-light)', borderRadius: 4, font: 'inherit',
               padding: '4px 2px', opacity: canVmax ? 1 : 0.6 }}
    >
      {status?.vmax == null && <option value="">vmax ?</option>}
      {choices.map((c) => <option key={c} value={String(c)}>{c} m/s</option>)}
    </select>
  ) : null;

  const fsBar = fullscreen || !barSlot;
  const bar: React.CSSProperties = {
    zIndex: 6, color: 'var(--color-text-primary)', font: '11px/1.45 system-ui, sans-serif',
    background: 'var(--color-bg-overlay)', borderTop: '1px solid var(--color-border)', padding: '6px 8px',
    ...(fsBar ? { position: 'absolute', left: 0, right: 0, bottom: 0 } : {}),
  };
  const btn: React.CSSProperties = {
    background: 'var(--color-bg-raised)', border: '1px solid var(--color-border-light)',
    color: 'var(--color-text-primary)', borderRadius: 4, cursor: 'pointer',
    padding: '5px 8px', font: 'inherit', fontWeight: 600,
  };
  const mono: React.CSSProperties = { fontFamily: 'ui-monospace, monospace' };
  const needMode = modeMismatch(status);
  const reason = friendlyReason(g?.reason);
  const hint = !usable ? null
    : drawable ? 'Drag on the video to target'
      : status?.cut === false ? 'CUT to re-target' : null;

  const controls = (
    <div style={bar} onMouseDown={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span
          title={heartbeatOk ? 'The Pi is hearing this page'
            : heartbeatWhy ? `This page is not keeping it engaged — ${heartbeatWhy}`
              : 'The Pi is NOT hearing this page — it will cut'}
          style={{ width: 8, height: 8, borderRadius: 4, flex: 'none',
                   background: heartbeatOk ? 'var(--color-accent)' : 'var(--color-danger)' }}
        />
        <strong style={{ color: TONE[head.tone], letterSpacing: 0.4 }}>{head.label}</strong>
        {hint && <span style={{ color: 'var(--color-text-secondary)' }}>· {hint}</span>}
        <span style={{ flex: 1 }} />
        <button
          style={{ ...btn, padding: '1px 6px', fontWeight: 400 }}
          onClick={() => setCollapsedChoice(!collapsed)}
          aria-label={collapsed ? 'Show guidance details' : 'Hide guidance details'}
          title={collapsed ? 'Show details' : 'Hide details'}
        >
          {collapsed ? '+' : '–'}
        </button>
      </div>
      {/* CUT first and always in the same place, whatever the status line says. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        <button
          style={{ ...btn, background: 'var(--color-danger-fill)', borderColor: 'var(--color-danger-fill)',
                   color: 'var(--color-on-danger)', fontSize: 13, padding: '5px 18px' }}
          onClick={() => call('cut')}
          title="Stop: one stop setpoint, then the Pi sends nothing until Resume"
        >CUT</button>
        <button
          style={{ ...btn, background: 'var(--color-accent)', borderColor: 'var(--color-accent)',
                   color: 'var(--color-on-accent)', opacity: usable ? 1 : 0.5 }}
          disabled={!usable}
          onClick={() => call('resume')}
          title="The Pi's Resume: let it command the aircraft. It still sends nothing until the pilot selects GUIDED."
        >RESUME</button>
        <button style={btn} disabled={!usable} onClick={() => call('clear')}
                title="Drop the lock">RELEASE</button>
        <button style={btn} disabled={!usable} onClick={() => call('reseed')}
                title="Re-learn the target's look from the current box">RE-SEED</button>
        {vmaxSelect}
      </div>

      {!usable && status?.reason && (
        <div style={{ marginTop: 4, color: 'var(--color-warning)' }}>{status.reason}</div>
      )}
      {usable && collapsed && (reason || needMode) && (
        <div style={{ marginTop: 4, color: 'var(--color-text-secondary)', ...mono }}>
          {needMode ? `needs ${needMode}` : ''}{needMode && reason ? ' · ' : ''}{reason}
        </div>
      )}
      {usable && !collapsed && (
        <div style={{ marginTop: 4, color: 'var(--color-text-secondary)', ...mono }}>
          <div>
            {ac.mode || 'mode ?'} {ac.armed === true ? '· ARMED' : ac.armed === false ? '· disarmed' : ''}
            {ac.alt !== null ? ` · ${ac.alt.toFixed(1)} m` : ''}
            {g?.pretend ? ' · PRETEND' : ''}
            {needMode ? ` · needs ${needMode}` : ''}
          </div>
          <div>
            lock {g?.track || 'none'} · conf {g?.conf != null ? g.conf.toFixed(2) : '—'}
            {g?.lock_age != null ? ` · age ${g.lock_age}` : ''}
          </div>
          {reason && <div title={g?.reason ?? undefined}>{reason}</div>}
          <div style={{ color: 'var(--color-text-primary)' }}>
            sending: {describeCommand(status?.allowed)}
          </div>
          {!status?.allowed && status?.decided && (
            <div>would send: {describeCommand(status.decided)}</div>
          )}
          {status?.telemetry?.connected && (
            <div>
              bat {status.telemetry.battery ?? '—'}% · sats {status.telemetry.satellites ?? '—'}
              {status.telemetry.fix_type != null && status.telemetry.fix_type < 3 ? ' · NO 3D FIX' : ''}
            </div>
          )}
          {g?.note && <div style={{ color: 'var(--color-warning)' }}>{g.note}</div>}
          {status?.controller && (
            <div>{status.you_control ? 'you hold control' : `controlled by ${status.controller}`}</div>
          )}
        </div>
      )}
      {message && <div style={{ marginTop: 4, color: 'var(--color-warning)' }}>{message}</div>}
    </div>
  );

  return (
    <>
      {drawable && rect.width > 0 && (
        <div
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          title="Drag a box around the target"
          style={{
            position: 'absolute', left: rect.left, top: rect.top, width: rect.width, height: rect.height,
            zIndex: 5, cursor: 'crosshair', touchAction: 'none',
          }}
        />
      )}
      {preview && (
        <div style={{
          position: 'absolute', zIndex: 5, pointerEvents: 'none', ...preview,
          border: '2px solid var(--color-warning)',
        }} />
      )}
      {fsBar ? controls : createPortal(controls, barSlot as HTMLElement)}
    </>
  );
}
